import React, { useState } from "react";
import { Box, Typography, CircularProgress, Menu, MenuItem, Button, Popover, Checkbox, FormControlLabel, TextField, Select, FormControl, InputLabel, Chip, IconButton } from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CloseIcon from '@mui/icons-material/Close';

const FigmaDataWorkspace = ({
  rows,
  columns,
  totalCount,
  loading,
  paginationModel,
  setPaginationModel,
  handleViewClick,
  keyType
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const filterOpen = Boolean(filterAnchorEl);
  const columnOpen = Boolean(columnAnchorEl);
  
  // Initialize all columns as visible by default
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const initialVisibility = {};
    columns.forEach(col => {
      initialVisibility[col.field] = true; // ALL columns visible by default
    });
    return initialVisibility;
  });
  
  // Filter state
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState([]);
  
  // Filter operators
  const operators = [
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Does not contain' },
    { value: 'equals', label: 'Equals' },
    { value: 'startsWith', label: 'Starts with' },
    { value: 'endsWith', label: 'Ends with' },
    { value: 'isEmpty', label: 'Is empty' },
    { value: 'isNotEmpty', label: 'Is not empty' }
  ];

  // Update visible columns when columns prop changes
  React.useEffect(() => {
    const newVisibility = {};
    columns.forEach(col => {
      // Preserve existing visibility if column already exists, otherwise show by default
      if (visibleColumns[col.field] !== undefined) {
        newVisibility[col.field] = visibleColumns[col.field];
      } else {
        newVisibility[col.field] = true; // Show new columns by default
      }
    });
    setVisibleColumns(newVisibility);
  }, [columns]);

  // Filter local rows based on search query and column filters
  const filteredRows = rows.filter((row) => {
    // Search query filter
    let matchesSearch = true;
    if (searchQuery) {
      matchesSearch = Object.values(row).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Column filters
    let matchesColumnFilters = true;
    for (const [field, filter] of Object.entries(columnFilters)) {
      if (!filter || !filter.value) continue;
      
      const cellValue = String(row[field] ?? "").toLowerCase();
      const filterValue = String(filter.value).toLowerCase();
      
      switch (filter.operator) {
        case 'contains':
          if (!cellValue.includes(filterValue)) matchesColumnFilters = false;
          break;
        case 'notContains':
          if (cellValue.includes(filterValue)) matchesColumnFilters = false;
          break;
        case 'equals':
          if (cellValue !== filterValue) matchesColumnFilters = false;
          break;
        case 'startsWith':
          if (!cellValue.startsWith(filterValue)) matchesColumnFilters = false;
          break;
        case 'endsWith':
          if (!cellValue.endsWith(filterValue)) matchesColumnFilters = false;
          break;
        case 'isEmpty':
          if (cellValue !== "") matchesColumnFilters = false;
          break;
        case 'isNotEmpty':
          if (cellValue === "") matchesColumnFilters = false;
          break;
        default:
          break;
      }
      
      if (!matchesColumnFilters) break;
    }
    
    return matchesSearch && matchesColumnFilters;
  });

  // Apply pagination to filtered rows (8 rows per page)
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = startIndex + paginationModel.pageSize;
  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  // Get visible columns for rendering
  const visibleColumnsList = columns.filter(col => visibleColumns[col.field]);

  // Get table headers for export
  const getTableHeaders = () => {
    const headers = ["S.No"];
    visibleColumnsList.forEach(col => {
      headers.push(col.headerName);
    });
    return headers;
  };

  // Get table data for all filtered rows
  const getTableData = () => {
    const data = [];
    filteredRows.forEach((row, idx) => {
      const rowData = [String(idx + 1)];
      visibleColumnsList.forEach(col => {
        let value = row[col.field] ?? "-";
        if (typeof value === 'object') {
          value = JSON.stringify(value);
        }
        rowData.push(String(value));
      });
      data.push(rowData);
    });
    return data;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = getTableHeaders();
    const data = getTableData();
    
    const csvContent = [headers, ...data]
      .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${metadata.title}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Print functionality
  const handlePrint = () => {
    const headers = getTableHeaders();
    const data = getTableData();
    
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleString();
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${metadata.title} Report - ${currentDate}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Outfit', 'Segoe UI', Arial, sans-serif; padding: 40px; background: white; }
          .report-header { margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #E7E5E0; }
          .report-title { font-size: 24px; font-weight: 600; color: #1A1917; margin-bottom: 8px; }
          .report-subtitle { font-size: 14px; color: #78716C; margin-bottom: 8px; }
          .report-meta { font-size: 12px; color: #A8A29E; margin-top: 8px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #F5F5F4; color: #1A1917; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; padding: 12px; text-align: left; border: 1px solid #E7E5E0; }
          td { padding: 10px 12px; border: 1px solid #E7E5E0; color: #57534E; font-size: 13px; }
          tr:hover { background-color: #FAFAFA; }
          .status-badge { display: inline-block; padding: 4px 8px; border-radius: 6px; font-size: 11px; font-weight: 500; }
          .status-confirmed, .status-completed { background: #F0FDF4; color: #16A34A; }
          .status-pending { background: #FFFBEB; color: #D97706; }
          .status-cancelled { background: #FEF2F2; color: #DC2626; }
          @media print {
            body { padding: 20px; }
            th { background: #F5F5F4 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .status-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-title">${metadata.title} Report</div>
          <div class="report-subtitle">${metadata.sub}</div>
          <div class="report-meta">Generated on: ${currentDate} | Total Records: ${filteredRows.length}</div>
        </div>
        <table>
          <thead><tr>${headers.map(header => `<th>${header}</th>`).join('')}</tr></thead>
          <tbody>
            ${data.map(row => `
              <tr>
                ${row.map(cell => {
                  const isStatus = headers[row.indexOf(cell)]?.toLowerCase().includes('status');
                  if (isStatus && (cell === 'Confirmed' || cell === 'Completed')) {
                    return `<td><span class="status-badge status-completed">${cell}</span></td>`;
                  } else if (isStatus && (cell === 'Pending' || cell === 'In Progress')) {
                    return `<td><span class="status-badge status-pending">${cell}</span></td>`;
                  } else if (isStatus && cell === 'Cancelled') {
                    return `<td><span class="status-badge status-cancelled">${cell}</span></td>`;
                  }
                  return `<td>${cell}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div style="margin-top: 30px; text-align: center; font-size: 11px; color: #A8A29E;">
          This report was generated automatically. For support, contact your system administrator.
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => { printWindow.print(); };
  };

  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportOption = (option) => {
    if (option === 'print') {
      handlePrint();
    } else if (option === 'csv') {
      exportToCSV();
    }
    handleClose();
  };

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const addFilter = (field, operator, value) => {
    const newFilter = { field, operator, value, columnName: columns.find(c => c.field === field)?.headerName };
    setColumnFilters(prev => ({ ...prev, [field]: { operator, value } }));
    setActiveFilters(prev => [...prev, newFilter]);
    handleFilterClose();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const removeFilter = (field) => {
    const newFilters = { ...columnFilters };
    delete newFilters[field];
    setColumnFilters(newFilters);
    setActiveFilters(activeFilters.filter(f => f.field !== field));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setActiveFilters([]);
    setSearchQuery("");
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Column visibility handlers
  const handleColumnClick = (event) => {
    setColumnAnchorEl(event.currentTarget);
  };

  const handleColumnClose = () => {
    setColumnAnchorEl(null);
  };

  const toggleColumn = (field) => {
    setVisibleColumns(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const showAllColumns = () => {
    const allVisible = {};
    columns.forEach(col => {
      allVisible[col.field] = true;
    });
    setVisibleColumns(allVisible);
  };

  const hideAllColumns = () => {
    const allHidden = {};
    columns.forEach(col => {
      allHidden[col.field] = false;
    });
    setVisibleColumns(allHidden);
  };

  // Dynamic conditional coloring specs for table status columns
  const getStatusBadgeStyles = (status) => {
  const normalized = String(status).trim().toLowerCase();

  // Light Green
  if (
    normalized === "booking confirmed" ||
    normalized === "confirmed"
  ) {
    return { bg: "#FFFFFF", text: "#22DA66", dot: "#22DA66" };
  }

  // Dark Green
  if (normalized === "enroute") {
    return { bg: "#FFFFFF", text: "#006E29", dot: "#006E29" };
  }

  // Blue
  if (
    normalized === "completed trip" ||
    normalized === "trip completed"
  ) {
    return { bg: "#FFFFFF", text: "#2563EB", dot: "#2563EB" };
  }

  // Yellow
  if (normalized === "inquiry") {
    return { bg: "#FFFFFF", text: "#D97706", dot: "#D97706" };
  }

  // Dark Blue
  if (
    normalized === "document submitted" ||
    normalized === "verification pending"
  ) {
    return { bg: "#FFFFFF", text: "#070079", dot: "#070079" };
  }

  // Red
  if (
    normalized === "cancelled" ||
    normalized === "canceled"
  ) {
    return { bg: "#FFFFFF", text: "#DA2D21", dot: "#DA2D21" };
  }

  // Black
  if (normalized === "enquiry closed") {
    return { bg: "#FFFFFF", text: "#000000", dot: "#000000" };
  }

  // Fallback Grey
  return { bg: "#FFFFFF", text: "#78716C", dot: "#78716C" };
};

  // Humanize tab labels
  const getContextMetadata = () => {
    switch (keyType) {
      case "vehicleBookings":
        return { title: "Bookings", sub: "Customer booking records and payment details" };
      case "vehicleIssues":
        return { title: "Issues", sub: "Vehicle failure reports, breakdowns, and maintenance tickets" };
      case "vehicleDocuments":
        return { title: "Documents", sub: "Registration papers, insurance indices, and statutory permits" };
      case "fuelLogs":
        return { title: "Fuel History", sub: "Fuel consumption records and history" };
      default:
        return { title: "Asset Logs", sub: "Granular breakdown records for the selected vehicle fleet" };
    }
  };

  const metadata = getContextMetadata();
  const filteredTotalCount = filteredRows.length;
  const totalPages = Math.ceil(filteredTotalCount / paginationModel.pageSize) || 1;

  // Reset to first page when search query changes
  React.useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [searchQuery, columnFilters]);

  // Filter selection state
  const [selectedFilterColumn, setSelectedFilterColumn] = useState("");
  const [selectedOperator, setSelectedOperator] = useState("contains");
  const [filterValue, setFilterValue] = useState("");

  // Debug: Log visible columns count
  React.useEffect(() => {
    console.log(`Total columns: ${columns.length}, Visible columns: ${visibleColumnsList.length}`);
  }, [columns, visibleColumns]);

  return (
    <div style={{ width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.03), 0px 1px 3px rgba(0, 0, 0, 0.04)', overflow: 'hidden', borderRadius: 16, outline: '0.80px #E7E5E0 solid', outlineOffset: '-0.80px', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'inline-flex', fontFamily: 'Outfit, sans-serif' }}>
      
      {/* HEADER SECTION METADATA */}
      <div style={{ alignSelf: 'stretch', paddingLeft: 28, paddingRight: 28, paddingTop: 20, paddingBottom: 20, borderBottom: '0.80px #EEEDE9 solid', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
        <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'flex' }}>
          <div style={{ width: 36, height: 36, background: '#F0EFE9', borderRadius: 16, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
            <div style={{ width: 15, height: 15, position: 'relative', overflow: 'hidden' }}>
              <div style={{ width: 12.50, height: 6.25, left: 1.25, top: 4.38, position: 'absolute', outline: '1.25px #4F46E5 solid', outlineOffset: '-0.63px' }} />
              <div style={{ width: 2.50, height: 2.50, left: 3.13, top: 9.38, position: 'absolute', outline: '1.25px #4F46E5 solid', outlineOffset: '-0.63px' }} />
              <div style={{ width: 2.50, height: 2.50, left: 9.38, top: 9.38, position: 'absolute', outline: '1.25px #4F46E5 solid', outlineOffset: '-0.63px' }} />
            </div>
          </div>
          <div style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'inline-flex' }}>
            <div style={{ color: '#1A1917', fontSize: 16, fontWeight: '600', lineHeight: '19.2px', fontFamily: 'Outfit, sans-serif' }}>{metadata.title}</div>
            <div style={{ paddingTop: 2, color: '#A8A29E', fontSize: 13, fontWeight: '400', lineHeight: '19.5px', fontFamily: 'Outfit, sans-serif' }}>{metadata.sub}</div>
          </div>
        </div>
        <div style={{ paddingLeft: 12, paddingRight: 12, height: 30, background: '#F0EFE9', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#78716C', fontSize: 12, fontWeight: '500', fontFamily: 'Outfit, sans-serif' }}>{filteredTotalCount} records</span>
        </div>
      </div>

      {/* FILTER CONTROL BAR STRIP */}
      <div style={{ alignSelf: 'stretch', paddingLeft: 28, paddingRight: 28, paddingTop: 16, paddingBottom: 16, borderBottom: '0.80px #EEEDE9 solid', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
          {/* Search Input */}
          <div style={{ width: 256, paddingLeft: 16, paddingRight: 16, paddingTop: 10, paddingBottom: 10, background: 'white', borderRadius: 12, outline: '0.80px #E7E5E0 solid', outlineOffset: '-0.80px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 13, height: 13, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="5.5" cy="5.5" r="4" stroke="#A8A29E" strokeWidth="1.2"/>
                <path d="M8.5 8.5L11.5 11.5" stroke="#A8A29E" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </div>
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search entries..."
              style={{ width: '100%', border: 'none', outline: 'none', color: '#1A1917', fontSize: 14, fontWeight: '400', fontFamily: 'Outfit, sans-serif' }}
            />
          </div>

          {/* Filter Button */}
          <Button
            onClick={handleFilterClick}
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{
              textTransform: 'none',
              fontFamily: 'Outfit, sans-serif',
              borderRadius: '12px',
              padding: '6px 16px',
              fontSize: '13px',
              fontWeight: '500',
              borderColor: '#E7E5E0',
              color: '#1A1917',
              '&:hover': {
                borderColor: '#4F46E5',
                backgroundColor: '#F5F3FF'
              }
            }}
          >
            Filter
          </Button>

          {/* Column Visibility Button */}
          <Button
            onClick={handleColumnClick}
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            sx={{
              textTransform: 'none',
              fontFamily: 'Outfit, sans-serif',
              borderRadius: '12px',
              padding: '6px 16px',
              fontSize: '13px',
              fontWeight: '500',
              borderColor: '#E7E5E0',
              color: '#1A1917',
              '&:hover': {
                borderColor: '#4F46E5',
                backgroundColor: '#F5F3FF'
              }
            }}
          >
            Columns
          </Button>

          {/* Active Filters Chips */}
          {activeFilters.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {activeFilters.map((filter) => (
                <Chip
                  key={filter.field}
                  label={`${filter.columnName} ${filter.operator} ${filter.value || ''}`}
                  onDelete={() => removeFilter(filter.field)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '12px',
                    height: '28px',
                    backgroundColor: '#F5F5F4',
                    '& .MuiChip-label': { padding: '0 8px' },
                    '& .MuiChip-deleteIcon': { fontSize: '14px', marginRight: '4px' }
                  }}
                />
              ))}
              <button
                onClick={clearAllFilters}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#EF4444',
                  fontSize: '12px',
                  fontFamily: 'Outfit, sans-serif',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
        
        {/* Download Button */}
        <div>
          <Button
            onClick={handleDownloadClick}
            variant="contained"
            startIcon={<FileDownloadIcon />}
            sx={{
              textTransform: 'none',
              fontFamily: 'Outfit, sans-serif',
              background: '#1A1917',
              borderRadius: '12px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              '&:hover': {
                background: '#2D2A26'
              }
            }}
          >
            Download Report
          </Button>
          
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: '12px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                minWidth: '180px',
                '& .MuiMenuItem-root': {
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '13px',
                  padding: '8px 16px',
                  gap: '12px',
                }
              }
            }}
          >
            <MenuItem onClick={() => handleExportOption('print')}>
              <PrintIcon sx={{ fontSize: '18px', color: '#78716C' }} />
              <span>Print Report</span>
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#A8A29E' }}>Ctrl+P</span>
            </MenuItem>
            <MenuItem onClick={() => handleExportOption('csv')}>
              <DescriptionIcon sx={{ fontSize: '18px', color: '#78716C' }} />
              <span>Export as CSV</span>
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#A8A29E' }}>.csv</span>
            </MenuItem>
          </Menu>
        </div>
      </div>

      {/* Filter Popover */}
      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            width: '400px',
            p: 2,
            fontFamily: 'Outfit, sans-serif'
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#1A1917', fontFamily: 'Outfit, sans-serif' }}>Add Filter</span>
            <IconButton size="small" onClick={handleFilterClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: 'Outfit, sans-serif' }}>Column</InputLabel>
            <Select
              value={selectedFilterColumn}
              onChange={(e) => setSelectedFilterColumn(e.target.value)}
              label="Column"
              sx={{ fontFamily: 'Outfit, sans-serif', borderRadius: '12px' }}
            >
              {columns.map((col) => (
                <MenuItem key={col.field} value={col.field} sx={{ fontFamily: 'Outfit, sans-serif' }}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: 'Outfit, sans-serif' }}>Operator</InputLabel>
            <Select
              value={selectedOperator}
              onChange={(e) => setSelectedOperator(e.target.value)}
              label="Operator"
              sx={{ fontFamily: 'Outfit, sans-serif', borderRadius: '12px' }}
            >
              {operators.map((op) => (
                <MenuItem key={op.value} value={op.value} sx={{ fontFamily: 'Outfit, sans-serif' }}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {selectedOperator !== 'isEmpty' && selectedOperator !== 'isNotEmpty' && (
            <TextField
              size="small"
              label="Value"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': { fontFamily: 'Outfit, sans-serif' },
                '& .MuiInputBase-root': { fontFamily: 'Outfit, sans-serif', borderRadius: '12px' }
              }}
            />
          )}
          
          <Button
            variant="contained"
            onClick={() => {
              if (selectedFilterColumn) {
                addFilter(selectedFilterColumn, selectedOperator, filterValue);
                setSelectedFilterColumn("");
                setSelectedOperator("contains");
                setFilterValue("");
              }
            }}
            disabled={!selectedFilterColumn || (selectedOperator !== 'isEmpty' && selectedOperator !== 'isNotEmpty' && !filterValue)}
            sx={{
              textTransform: 'none',
              fontFamily: 'Outfit, sans-serif',
              background: '#4F46E5',
              borderRadius: '12px',
              '&:hover': { background: '#4338CA' }
            }}
          >
            Apply Filter
          </Button>
        </div>
      </Popover>

      {/* Column Visibility Popover */}
      <Popover
        open={columnOpen}
        anchorEl={columnAnchorEl}
        onClose={handleColumnClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            width: '280px',
            p: 2,
            fontFamily: 'Outfit, sans-serif'
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#1A1917', fontFamily: 'Outfit, sans-serif' }}>Show/Hide Columns</span>
            <IconButton size="small" onClick={handleColumnClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          
          <div style={{ display: 'flex', gap: 8, borderBottom: '0.80px solid #EEEDE9', paddingBottom: 8 }}>
            <button
              onClick={showAllColumns}
              style={{
                background: 'none',
                border: 'none',
                color: '#4F46E5',
                fontSize: '12px',
                fontFamily: 'Outfit, sans-serif',
                cursor: 'pointer'
              }}
            >
              Show All
            </button>
            <span style={{ color: '#E7E5E0' }}>|</span>
            <button
              onClick={hideAllColumns}
              style={{
                background: 'none',
                border: 'none',
                color: '#4F46E5',
                fontSize: '12px',
                fontFamily: 'Outfit, sans-serif',
                cursor: 'pointer'
              }}
            >
              Hide All
            </button>
          </div>
          
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {columns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={visibleColumns[col.field] === true}
                    onChange={() => toggleColumn(col.field)}
                    sx={{
                      color: '#A8A29E',
                      '&.Mui-checked': { color: '#4F46E5' }
                    }}
                  />
                }
                label={col.headerName}
                sx={{
                  width: '100%',
                  margin: 0,
                  padding: '8px 0',
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '13px',
                    color: '#1A1917'
                  }
                }}
              />
            ))}
          </div>
        </div>
      </Popover>

      {/* SPREADSHEET TABLE GRID CONTAINER */}
      <div style={{ alignSelf: 'stretch', overflowX: 'auto', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ minWidth: visibleColumnsList.length > 0 ? (60 + visibleColumnsList.length * 140) : 300, width: '100%', display: 'flex', flexDirection: 'column' }}>
          
          {/* TABLE HEADERS */}
          <div style={{ height: 43.40, background: '#FDFDFD', borderBottom: '0.80px #EEEDE9 solid', boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.02)', display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 60, paddingLeft: 20, color: '#A8A29E', fontSize: 10, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.80, fontFamily: 'Outfit, sans-serif' }}>S.No</div>
            {visibleColumnsList.map((col, idx) => (
              <div key={col.field || idx} style={{ flex: col.flex || 1, minWidth: col.width || 120, paddingLeft: 20, color: '#A8A29E', fontSize: 10, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.80, fontFamily: 'Outfit, sans-serif' }}>
                {col.headerName}
              </div>
            ))}
          </div>

          {/* TABLE ROWS */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 270 }}>
            {loading ? (
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 270 }}>
                <CircularProgress size={30} style={{ color: '#4F46E5' }} />
              </Box>
            ) : paginatedRows.length === 0 ? (
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 270 }}>
                <Typography style={{ color: '#A8A29E', fontSize: 14, fontFamily: 'Outfit, sans-serif' }}>
                  {searchQuery || activeFilters.length > 0 ? "No matching records found" : "No data records found matching asset parameters"}
                </Typography>
              </Box>
            ) : (
              paginatedRows.map((row, index) => {
                const globalIndex = startIndex + index + 1;
                return (
                  <div 
                    key={row.id || index}
                    onClick={() => handleViewClick(row)}
                    style={{ alignSelf: 'stretch', height: 54, background: 'white', borderBottom: '0.80px #F0EFE9 solid', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    className="hover:bg-stone-50/60 transition-colors"
                  >
                    <div style={{ width: 60, paddingLeft: 20, color: '#C8C4BE', fontSize: 14, fontWeight: '400', fontFamily: 'Outfit, sans-serif' }}>{globalIndex}</div>
                    {visibleColumnsList.map((col, colIdx) => {
                      const isStatusField = String(col.field).toLowerCase().includes("status");
                      const cellValue = row[col.field] ?? "-";

                      return (
                        <div key={col.field || colIdx} style={{ flex: col.flex || 1, minWidth: col.width || 120, paddingLeft: 20, color: '#57534E', fontSize: 14, fontWeight: col.field === "amount" ? '500' : '400', fontFamily: 'Outfit, sans-serif' }}>
                          {isStatusField ? (
                            (() => {
                              const badge = getStatusBadgeStyles(cellValue);
                              return (
                                <div style={{ background: badge.bg, color: badge.text, display: 'inline-flex', padding: '2px 10px', borderRadius: 6, alignItems: 'center', gap: 6, fontSize: 11, fontWeight: '500', border: '0.80px #ECECEC solid', fontFamily: 'Outfit, sans-serif' }}>
                                  <span style={{ color: badge.dot, fontSize: 8 }}>●</span>
                                  {cellValue}
                                </div>
                              );
                            })()
                          ) : (
                            cellValue
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

        </div>
      </div>

      {/* PAGINATION FOOTER */}
      <div style={{ alignSelf: 'stretch', paddingLeft: 28, paddingRight: 28, paddingTop: 14, paddingBottom: 14, borderTop: '0.80px #EEEDE9 solid', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', background: 'white' }}>
        <div style={{ color: '#A8A29E', fontSize: 13, fontWeight: '400', fontFamily: 'Outfit, sans-serif' }}>
          Showing {filteredTotalCount === 0 ? 0 : startIndex + 1}–{Math.min(endIndex, filteredTotalCount)} of {filteredTotalCount} records
        </div>
        
        {/* PAGINATION CONTROLS */}
        <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'flex' }}>
          <button
            type="button"
            disabled={paginationModel.page === 0}
            onClick={() => setPaginationModel(prev => ({ ...prev, page: Math.max(0, prev.page - 1) }))}
            style={{ width: 32, height: 32, background: 'white', borderRadius: 12, border: '0.80px #E7E5E0 solid', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: paginationModel.page === 0 ? 'not-allowed' : 'pointer', opacity: paginationModel.page === 0 ? 0.3 : 1, fontFamily: 'Outfit, sans-serif' }}
            aria-label="Previous page"
          >
            ‹
          </button>

          {Array.from({ length: Math.min(totalPages, 5) }).map((_, pageIdx) => {
            let displayPage = pageIdx;
            if (totalPages > 5) {
              if (paginationModel.page > 2) {
                displayPage = paginationModel.page - 2 + pageIdx;
                if (displayPage >= totalPages) return null;
              }
            }
            
            const isCurrentPage = paginationModel.page === displayPage;
            if (displayPage < totalPages) {
              return (
                <button
                  key={displayPage}
                  type="button"
                  onClick={() => setPaginationModel(prev => ({ ...prev, page: displayPage }))}
                  style={{ width: 32, height: 32, background: isCurrentPage ? '#1A1917' : 'white', borderRadius: 12, border: isCurrentPage ? '0.80px #1A1917 solid' : '0.80px #E7E5E0 solid', color: isCurrentPage ? 'white' : '#57534E', fontSize: 12, fontWeight: '500', cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}
                >
                  {displayPage + 1}
                </button>
              );
            }
            return null;
          })}

          <button
            type="button"
            disabled={paginationModel.page + 1 >= totalPages}
            onClick={() => setPaginationModel(prev => ({ ...prev, page: Math.min(totalPages - 1, prev.page + 1) }))}
            style={{ width: 32, height: 32, background: 'white', borderRadius: 12, border: '0.80px #E7E5E0 solid', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: paginationModel.page + 1 >= totalPages ? 'not-allowed' : 'pointer', opacity: paginationModel.page + 1 >= totalPages ? 0.3 : 1, fontFamily: 'Outfit, sans-serif' }}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>

    </div>
  );
};

export default FigmaDataWorkspace;